import { useState } from 'react';
import { Text, Toast } from '@belong/ui';
import { MultiselectDateTimeRangePicker } from 'accounts/components/multiselect-date-time-range-picker/multiselect-date-time-range-picker';
import { StepFormLayout } from 'accounts/components/step-form-layout';
import { HOME_INSPECTION_STRINGS } from 'accounts/constants/strings/home-inspection.strings';
import { useSetupFlowStep } from 'accounts/hooks/homeowners/use-setup-flow-step-context';
import { getDateTimeValidationState } from 'accounts/utils/date-time-validation';
import { useInspectionScheduleStorage } from 'accounts/hooks/use-inspection-schedule-storage';
import Field from 'components/Field/Field';
import { InputFinalFormAdapter } from 'components/Input/Input';
import { template } from '@belong/common';

export function HomeInspectionSchedule() {
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [isValidationToastOpen, setIsValidationToastOpen] = useState(false);
  const [showDateValidationErrors, setShowDateValidationErrors] =
    useState(false);
  const [showTimeValidationErrors, setShowTimeValidationErrors] =
    useState(false);

  const {
    selectedSelections,
    selectedDate,
    specificRequests,
    setSpecificRequests,
    updateData,
  } = useInspectionScheduleStorage();

  const {
    currentStepFormData,
    goPreviousStep,
    handleSaveAndNext,
    selectedUnit,
    setLoading,
  } = useSetupFlowStep();
  const { isHandledByAnExternalProvider = false } = currentStepFormData || {};

  function getInitialValues() {
    return {
      specificRequests:
        specificRequests || currentStepFormData?.specificRequests || '',
      startTimeOptions: [],
    };
  }

  async function handleSubmit(values: any) {
    try {
      // Check validation before proceeding
      const config = { minSelections: 3, isHandledByAnExternalProvider };
      const validationResult = getDateTimeValidationState(
        selectedSelections,
        selectedDate,
        config,
      );

      if (validationResult.shouldShowToast) {
        setIsValidationToastOpen(true);
        setShowDateValidationErrors(
          validationResult.validationState.showDateValidationErrors,
        );
        setShowTimeValidationErrors(
          validationResult.validationState.showTimeValidationErrors,
        );
        return;
      }

      const { specificRequests } = values;

      setSpecificRequests(specificRequests);

      const payload: {
        specificRequests: string[];
        startTime?: string;
        startTimeOptions?: string[];
      } = {
        specificRequests,
      };

      // If handled by external provider, send only the first available time as startTime
      if (isHandledByAnExternalProvider && selectedSelections.length > 0) {
        const allTimes = selectedSelections.flatMap((s) => s.availableTimes);
        const uniqueTimes = [...new Set(allTimes)].sort();
        const [firstTimeOption] = uniqueTimes;
        payload.startTime = firstTimeOption;
      } else if (selectedSelections.length > 0) {
        const allTimes = selectedSelections.flatMap((s) => s.availableTimes);
        const uniqueTimes = [...new Set(allTimes)].sort();
        payload.startTimeOptions = uniqueTimes;
      }

      await handleSaveAndNext(payload);
    } catch {
      setLoading(false);
      setIsToastOpen(true);
    }
  }

  function handleToastClose() {
    setIsToastOpen(false);
  }

  function handleValidationToastClose() {
    setIsValidationToastOpen(false);
  }
  const subTitle = template(HOME_INSPECTION_STRINGS.sub_title_schedule, {
    timeText: isHandledByAnExternalProvider ? 'a time' : 'at least 3 times',
    pluralSuffix: isHandledByAnExternalProvider ? 's' : '',
  });

  return (
    <>
      <StepFormLayout
        title={HOME_INSPECTION_STRINGS.title_schedule}
        subTitle={subTitle}
        onSubmit={handleSubmit}
        initialValues={getInitialValues()}
        handleBackStep={goPreviousStep}
        disableProgressBar
        doneLabel="Done"
        getForm={() => {
          return (
            <div className="relative top-sm">
              <MultiselectDateTimeRangePicker
                unitId={selectedUnit?.basicInfo?.id}
                timeZone={selectedUnit?.basicInfo.timeZone}
                title={template(HOME_INSPECTION_STRINGS.calendar_instruction, {
                  count: isHandledByAnExternalProvider ? 1 : 3,
                  timesLabel: isHandledByAnExternalProvider ? 'Time' : 'Times',
                })}
                maxSelections={isHandledByAnExternalProvider ? 1 : 10}
                minSelections={isHandledByAnExternalProvider ? 1 : 3}
                initialSelections={selectedSelections}
                showDateValidationErrors={showDateValidationErrors}
                showTimeValidationErrors={showTimeValidationErrors}
                onDateValidationReset={() => setShowDateValidationErrors(false)}
                onTimeValidationReset={() => setShowTimeValidationErrors(false)}
                onChange={(selections, selectedDate) => {
                  updateData({ selectedSelections: selections, selectedDate });
                  setIsValidationToastOpen(false);
                  setShowDateValidationErrors(false);
                  setShowTimeValidationErrors(false);
                }}
              />

              <div className="mt-2xl w-full">
                <Text as="span" variant="h3" fontWeight="semibold">
                  {HOME_INSPECTION_STRINGS['specific.request.title']}
                </Text>
              </div>
              <div className="mb-2xl">
                <Field
                  name="specificRequests"
                  placeholder={
                    HOME_INSPECTION_STRINGS['specific_requests.placeholder']
                  }
                  textarea
                  component={InputFinalFormAdapter}
                />
              </div>
            </div>
          );
        }}
      />
      <Toast
        isVisible={isToastOpen}
        autocloseDuration={5000}
        variant="danger"
        onClose={handleToastClose}
      >
        An error occurred while scheduling your inspection. Please try another
        date and time.
      </Toast>
      <Toast
        isVisible={isValidationToastOpen}
        autocloseDuration={5000}
        variant="danger"
        onClose={handleValidationToastClose}
      >
        Please select{' '}
        {isHandledByAnExternalProvider ? '1 time' : 'at least 3 times'}
      </Toast>
    </>
  );
}
